/**
 * <EmptyVerdict> — F044 §9.1. The ONLY thing rendered on a zero denominator (the live Audit Workspace
 * "NOT READY" pattern): Status / Why / Impact / Action. Single CTA, no fear copy ("risky code can
 * reach production undetected"), no marketing copy. Renders the `Verdict.kind === 'State'` shape
 * `<VerdictPill>` refuses.
 */
import type { FC } from 'react';
import { Body1, Button, Caption1, Title3, makeStyles, tokens } from '@fluentui/react-components';
import { useNavigate } from 'react-router-dom';
import type { Verdict } from '../../types';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXL,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  status: {
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  meta: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
});

export interface EmptyVerdictProps {
  verdict: Verdict;
}

/** Renders the Status / Why / Impact / Action structure for a state (`kind === 'State'`) verdict. */
export const EmptyVerdict: FC<EmptyVerdictProps> = ({ verdict }) => {
  const styles = useStyles();
  const navigate = useNavigate();

  return (
    <div className={styles.root}>
      <Caption1 className={styles.status}>{verdict.status}</Caption1>
      <Title3>{verdict.label}</Title3>
      <div className={styles.meta}>
        {verdict.why && <Body1>{verdict.why}</Body1>}
        {verdict.impact && <Body1>{verdict.impact}</Body1>}
      </div>
      {verdict.action && (
        <Button appearance="primary" onClick={() => navigate(verdict.action!.route)}>
          {verdict.action.label}
        </Button>
      )}
    </div>
  );
};
